/**
 * Club Data Prefetcher
 * 
 * Component that eagerly prefetches club data when clubId becomes available.
 * This prevents race conditions where useClubQuery might be disabled
 * when components mount.
 * 
 * Usage: Add to App.tsx at the root level to ensure club data
 * is always fetched as soon as clubId is available.
 * 
 * @module services/internalApi/club/components
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { captureException } from '@butter/monitoring/web';
import { useClubId } from '~/services/appRuntime';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { getClubById } from '../api/clubService';

/**
 * ClubDataPrefetcher Component
 * 
 * Watches for clubId changes and prefetches club data immediately.
 * This ensures that useClubQuery will have data available in cache
 * when components mount, preventing the "missing club data" issue.
 */
export function ClubDataPrefetcher() {
  const queryClient = useQueryClient();
  const [clubId] = useClubId();
  const clubIdNumber = clubId ? Number(clubId) : null;

  useEffect(() => {
    if (!clubIdNumber) {
      console.warn('[ClubDataPrefetcher] No clubId available, skipping prefetch');
      return;
    }

    // Check if data is already in cache
    const cachedData = queryClient.getQueryData(queryKeys.club.detail(clubIdNumber));
    
    if (cachedData) {
      console.log('[ClubDataPrefetcher] Club data already in cache:', clubIdNumber);
      return;
    }

    console.log('[ClubDataPrefetcher] Prefetching club data for clubId:', clubIdNumber);

    // Prefetch club data using centralized query keys
    queryClient.prefetchQuery({
      queryKey: queryKeys.club.detail(clubIdNumber),
      queryFn: () => getClubById(clubIdNumber),
      staleTime: cachePresets.stable.staleTime,
    }).catch(error => {
      console.error('[ClubDataPrefetcher] Failed to prefetch club data:', error);
      captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { clubId: clubIdNumber! },
        extra: { url: window.location.href },
      });
    });
  }, [clubIdNumber, queryClient]);

  // This component renders nothing, it's just for the side effect
  return null;
}
